import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { completeCheck, failCheck } from '../../features/faceCheck/faceCheckSlice';
import FaceLiveness from './FaceLiveness';
import axios from 'axios';
import API_BASE_URL from '../../config';
import './FaceCheckModal.css';

const TIMEOUT_SECONDS = 60; // el coordinador tiene 60 segundos para responder

/**
 * Modal de verificación aleatoria durante el turno.
 * Se muestra cuando activeCheckIndex no es null.
 *
 * Si pasa: registra éxito en backend → cierra modal.
 * Si falla o no responde en 60s: registra fallo → bloquea sesión.
 */
function FaceCheckModal() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { activeCheckIndex } = useSelector((state) => state.faceCheck);

  const [timeLeft, setTimeLeft] = useState(TIMEOUT_SECONDS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isVisible = activeCheckIndex !== null;

  // Resetear timer cada vez que aparece el modal
  useEffect(() => {
    if (!isVisible) return;
    setTimeLeft(TIMEOUT_SECONDS);
    setError(null);
    setSubmitting(false);
  }, [isVisible]);

  // Countdown — si llega a 0, registrar fallo automáticamente
  useEffect(() => {
    if (!isVisible || submitting) return;

    if (timeLeft <= 0) {
      handleFail('timeout');
      return;
    }

    const tick = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(tick);
  }, [isVisible, timeLeft, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFail = useCallback(async (reason) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}auth/verify-face`,
        { descriptor: new Array(128).fill(0), checkNumber: activeCheckIndex + 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* el backend registrará el fallo */ }
    dispatch(failCheck());
  }, [submitting, activeCheckIndex, token, dispatch]);

  const handleLivenessConfirmed = useCallback(async (descriptor) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await axios.post(
        `${API_BASE_URL}auth/verify-face`,
        { descriptor, checkNumber: activeCheckIndex + 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        dispatch(completeCheck(activeCheckIndex));
      } else {
        setError('Rostro no reconocido. Intenta de nuevo.');
        setSubmitting(false);
        // Dar 15 segundos más para reintentar
        setTimeLeft((t) => Math.min(t + 15, TIMEOUT_SECONDS));
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
      setSubmitting(false);
    }
  }, [submitting, activeCheckIndex, token, dispatch]);

  if (!isVisible) return null;

  const timerPercent = (timeLeft / TIMEOUT_SECONDS) * 100;
  const timerColor = timeLeft > 30 ? '#4ade80' : timeLeft > 15 ? '#facc15' : '#f87171';

  return (
    <div className="fcheck-backdrop">
      <div className="fcheck-modal">
        <div className="fcheck-header">
          <div className="fcheck-badge">Verificación #{activeCheckIndex + 1} de 3</div>
          <h2 className="fcheck-title">Verificación de presencia</h2>
          <p className="fcheck-subtitle">
            Confirma que eres tú quien está coordinando la operación
          </p>
        </div>

        {/* Timer */}
        <div className="fcheck-timer-wrap">
          <div className="fcheck-timer-bar-bg">
            <div
              className="fcheck-timer-bar"
              style={{ width: `${timerPercent}%`, background: timerColor }}
            />
          </div>
          <span className="fcheck-timer-label" style={{ color: timerColor }}>
            {timeLeft}s restantes
          </span>
        </div>

        {error && (
          <div className="fcheck-error">
            <i className="fa fa-exclamation-triangle" /> {error}
          </div>
        )}

        {submitting && !error ? (
          <div className="fcheck-confirming">
            <div className="fcheck-spinner" />
            <p>Verificando...</p>
          </div>
        ) : (
          <FaceLiveness
            title=""
            onConfirmed={handleLivenessConfirmed}
            onError={(e) => setError(e)}
          />
        )}

        <button
          className="fcheck-skip-btn"
          onClick={() => handleFail('dismissed')}
          disabled={submitting}
        >
          No soy yo / No puedo verificar
        </button>
      </div>
    </div>
  );
}

export default FaceCheckModal;
